import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getHeatmap } from './request/request';
import { HeatmapData, algoColor, formatBytes } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import FreshnessBar from './FreshnessBar';
import Tooltip from '../Topology/components/Tooltip';

interface Props {
    timeRange: string;
}

const Srv6HeatmapTab: React.FC<Props> = ({ timeRange }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const res = await getHeatmap(token, timeRange);
            if (res.data?.data) {
                setHeatmapData(res.data.data);
                setLastFetched(new Date());
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load heatmap');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, timeRange]);

    useEffect(() => { load(); }, [load]);

    const heatmapMax = heatmapData
        ? Math.max(...heatmapData.matrix.flat(), 1)
        : 1;

    function heatmapCellColor(val: number): string {
        if (val === 0) return 'bg-semantic-bg-secondary text-semantic-text-secondary';
        const intensity = Math.min(val / heatmapMax, 1);
        if (intensity > 0.7) return 'bg-semantic-bg-danger-bold text-semantic-text-inverse-primary';
        if (intensity > 0.4) return 'bg-semantic-bg-warning-bold text-semantic-text-inverse-primary';
        if (intensity > 0.1) return 'bg-semantic-bg-success-secondary text-semantic-text-primary';
        return 'bg-semantic-bg-success-primary text-semantic-text-secondary';
    }

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;

    return (
        <div className="flex flex-col gap-4 overflow-auto p-2">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-semantic-text-primary">
                    Router x Algorithm Traffic
                </h2>
                <FreshnessBar lastFetched={lastFetched} onRefresh={load} />
            </div>
            <p className="text-[11px] text-semantic-text-secondary leading-relaxed -mt-2">
                Total bytes per router per flex-algo over the selected time window. Darker cells indicate higher volume.
            </p>
            {heatmapData ? (
                <div className="overflow-auto">
                    <table className="border-collapse text-[10px]">
                        <thead>
                            <tr>
                                <th className="p-1" />
                                {heatmapData.algorithms.map((a) => (
                                    <th
                                        key={a}
                                        className="p-1 text-center font-mono max-w-[60px] truncate"
                                        title={a}
                                        style={{ color: algoColor(a) }}
                                    >
                                        {a.length > 8 ? a.slice(0, 8) + '..' : a}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {heatmapData.routers.map((router, ri) => (
                                <tr key={router}>
                                    <td className="p-1 font-mono text-semantic-text-secondary whitespace-nowrap text-right pr-2">
                                        {router}
                                    </td>
                                    {heatmapData.algorithms.map((algo, ai) => {
                                        const val = heatmapData.matrix[ri]?.[ai] ?? 0;
                                        return (
                                            <td
                                                key={algo}
                                                className={`p-1 text-center min-w-[48px] border border-semantic-border-secondary/30 ${heatmapCellColor(val)}`}
                                                title={`${router} / ${algo}: ${formatBytes(val)}`}
                                            >
                                                {val === 0 ? '--' : formatBytes(val)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-xs text-semantic-text-secondary">No heatmap data</p>
            )}
            <div className="flex gap-4 text-[10px] text-semantic-text-secondary">
                <Tooltip text="No traffic observed for this router/algo pair." position="top">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-semantic-bg-secondary border border-semantic-border-secondary" /> 0 bytes
                    </span>
                </Tooltip>
                <Tooltip text="Below 10% of the maximum observed value." position="top">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-semantic-bg-success-secondary" /> Low
                    </span>
                </Tooltip>
                <Tooltip text="Between 10% and 70% of the maximum observed value." position="top">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-semantic-bg-warning-bold" /> Medium
                    </span>
                </Tooltip>
                <Tooltip text="Above 70% of the maximum observed value." position="top">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-semantic-bg-danger-bold" /> High
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};

export default Srv6HeatmapTab;
